import { Badge, Banner, Button, Field, Input } from '@acs/ui';
import { friendlyError } from '../../shared/api/errors.js';
import { claimsSourceNumber, toE164, toNsn, type Country } from '@acs/shared';
import { useEffect, useState } from 'react';
import type { BusinessDoc } from '../../shared/api/converters.js';
import { declaredSourceNumber } from '../../shared/lib/businessNumber.js';
import { formatPhoneDisplay } from '../../shared/lib/phoneFormat.js';
import { useMyBusinesses } from '../../app/BusinessesContext.js';
import { useDeclareSourceNumber } from './hooks.js';
import { useI18n } from '../../i18n/index.js';

/**
 * "Which number do your customers call?" — the declared business line (doc 18).
 *
 * This is the first thing the owner does in the number step and it is
 * **mandatory**, because it is what the forwarding check is compared against:
 * a call arrives claiming to have been diverted from some line, and we only
 * trust it if it is the line the owner told us about. Without a declaration
 * there is nothing to compare, and an unchecked claim cannot be allowed to
 * decide who receives someone's phone calls.
 *
 * Shared verbatim between the setup wizard and Settings › Number so the two can
 * never drift — the same reason `ForwardingPanel` is shared.
 *
 * Validation is deliberately client-side-optimistic and server-authoritative:
 * we pre-check with the same reducer the gateway uses so a typo is caught
 * before a round trip, but the stored value is always the server's canonical
 * form, echoed back into the field. If normalisation changed what they typed,
 * the owner sees it happen.
 */
export function BusinessLineField({
  business,
  onDeclared,
  locked = null,
}: {
  business: BusinessDoc;
  /** Fired after a successful save; `changed` is true when this replaced a
   * previously verified line, which invalidates the forwarding check. */
  onDeclared?: (sourceNumber: string, changed: boolean) => void;
  /** Why the field is read-only, or null when it is editable. `'check'` = a
   * verification window is open: the arriving call is compared against this
   * number, so it cannot move underneath it. The server refuses the write in
   * that state too, so this is the honest shape of the rule rather than a
   * disabled input hiding a rejection. */
  locked?: 'check' | null;
}) {
  const { t, tn } = useI18n();
  // Wherever the declaration currently lives: `pendingSourceNumber` before a
  // forwarding target exists, `forwardNumber.sourceNumber` after. Reading only
  // the latter left the field looking unsaved for the whole pre-provisioning
  // window — the exact stretch of setup where the owner has just saved it.
  const declared = declaredSourceNumber(business);
  const verified = !!business.forwardNumber?.sourceVerifiedAt;
  const declare = useDeclareSourceNumber(business.id);
  const { businesses } = useMyBusinesses();

  // The field holds the number EXACTLY as stored — E.164, no grouping. Numbers
  // are grouped for reading (`formatPhoneDisplay`, used in the hint below and
  // everywhere else the number is printed), but an input is not read, it is
  // typed into: spaces the owner did not type move the caret in ways they did
  // not ask for, and every keystroke would have to decide whether to re-space
  // what is already there.
  const [value, setValue] = useState(declared ?? '');
  const [touched, setTouched] = useState(false);
  useEffect(() => setValue(declared ?? ''), [declared]);

  const trimmed = value.trim();
  // Same reduction the server and the gateway apply — catching it here means an
  // obvious typo doesn't cost a round trip, and the rule can't quietly differ.
  const parts = toNsn(trimmed, business.country as Country);
  const looksValid = !!parts;
  // Everything below compares CANONICAL forms, never what was typed:
  // `+48 732 128 861` and `0048732128861` are one number, and a raw string
  // compare lets the spaced version slip past both of these checks.
  const canonical = parts ? toE164(parts) : null;
  const isOwnNumber = !!canonical && canonical === business.forwardNumber?.phoneNumber;
  // Dirty on the CANONICAL number where there is one: `+48 600 200 300` typed
  // over a stored `+48600200300` is the same line, and offering to save it
  // means offering a write that changes nothing but drops the forwarding proof.
  // Until the value parses there is nothing canonical to compare, so fall back
  // to the text — a half-typed number is a change, and Save has to be there
  // (disabled) to say why.
  const dirty = canonical != null ? canonical !== declared : trimmed !== (declared ?? '');

  // Is another of this owner's businesses already on this line?
  //
  // Checked here on the typed value rather than left to the server's rejection:
  // the owner finds out while their attention is still on the field, instead of
  // pressing Save and being bounced. The server enforces it regardless — this
  // list is one account's businesses, and a stale tab is a real thing.
  const duplicate = canonical
    ? (businesses.find((b) => b.id !== business.id && claimsSourceNumber(b, canonical)) ?? null)
    : null;

  // The duplicate is the one error shown before the field is blurred. It cannot
  // fire mid-word — it needs a fully parsed number that exactly matches another
  // business — so there is no half-typed noise to suppress, and waiting would
  // leave Save disabled with nothing on screen saying why.
  const error = duplicate
    ? t('businessLine.duplicate', { business: duplicate.name })
    : !touched
      ? undefined
      : isOwnNumber
        ? t('businessLine.ownNumber')
        : trimmed !== '' && !looksValid
          ? t('businessLine.invalid', { country: business.country })
          : undefined;

  return (
    <Field
      label={t('businessLine.label')}
      required
      hint={
        locked === 'check' ? (
          t('businessLine.lockedByCheck')
        ) : declared && !dirty && verified ? (
          <>
            {tn('businessLine.confirmed', {
              number: <span className="mono">{formatPhoneDisplay(declared)}</span>,
            })}
          </>
        ) : (
          t('businessLine.hint')
        )
      }
      error={error}
    >
      <div className="row" style={{ gap: 8, alignItems: 'flex-start' }}>
        <Input
          readOnly={locked !== null}
          mono
          inputMode="tel"
          autoComplete="tel"
          placeholder={t('businessLine.placeholder')}
          value={value}
          invalid={!!error}
          onBlur={() => setTouched(true)}
          onChange={(e) => setValue(e.target.value)}
          style={{ flex: 1 }}
        />
        {declared && !dirty && verified && <Badge tone="success">{t('businessLine.verified')}</Badge>}
        {dirty && locked === null && (
          <Button
            loading={declare.isPending}
            disabled={!looksValid || isOwnNumber || !!duplicate}
            onClick={() => {
              setTouched(true);
              declare.mutate(trimmed, {
                onSuccess: (res) => {
                  setValue(res.sourceNumber);
                  onDeclared?.(res.sourceNumber, res.reverificationRequired);
                },
              });
            }}
          >
            {t('common.save')}
          </Button>
        )}
      </div>
      {declare.isError && (
        <Banner tone="danger">
          {friendlyError(t, declare.error, t('businessLine.saveFailed'))}
        </Banner>
      )}
      {declare.data?.reverificationRequired && !dirty && (
        <Banner tone="warning">
          {t('businessLine.reverify')}
        </Banner>
      )}
    </Field>
  );
}
